import { makeObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { ComplianceAuditStore, ComplianceAuditQueryType } from './ComplianceAudit.store';
import { getCustomerDisplayName } from 'src/components/ComplianceAudits/utils/getCustomerDisplayName';

export enum DisplayNameStatus {
    LOADING = 'loading',
}

export type DisplayNameResultType = string | DisplayNameStatus.LOADING;

type ActorType = 'staff' | 'customer';

interface ParsedActorIdType {
    type: ActorType;
    id: number;
}

const ACTOR_ID_REGEX = /\w+:(?:staff|customer):\d+/g;

const parseActorId = (actorId: string): ParsedActorIdType | null => {
    const parts = actorId.split(':');
    if (parts.length >= 3) {
        const type = parts[parts.length - 2];
        const id = parseInt(parts[parts.length - 1] ?? '', 10);
        if ((type === 'staff' || type === 'customer') && !isNaN(id)) {
            return { type, id };
        }
    }
    return null;
};

export class ComplianceAuditState {
    public readonly store: ComplianceAuditStore;

    public constructor(
        private readonly query: ComplianceAuditQueryType,
        private readonly common: Common,
        private readonly models: ModelsState
    ) {
        makeObservable(this);
        this.store = new ComplianceAuditStore(this.common, this.query);
    }

    public getActorDisplayName(actorId: string): DisplayNameResultType {
        const parsed = parseActorId(actorId);
        if (parsed === null) {
            return actorId;
        }

        const userModel = this.models.getUserModel(parsed.id);

        if (parsed.type === 'customer') {
            const data = userModel.basicData.data;
            if (data === null) {
                return DisplayNameStatus.LOADING;
            }
            return getCustomerDisplayName(data);
        }

        const data = userModel.basicStaffData.data;
        if (data === null) {
            return DisplayNameStatus.LOADING;
        }
        const fullName = (data.fullName ?? '').trim();
        return fullName === '' ? data.name : fullName;
    }

    public getCustomerTooltipLabel(rawActorId: string): string {
        const accountId = rawActorId.split(':').pop() ?? '';
        if (this.query.kind === 'config') {
            return `Account ID: ${accountId}`;
        }
        return accountId === String(this.query.accountId)
            ? `Account ID: ${accountId} (this account)`
            : `Account ID: ${accountId}`;
    }

    public resolveActorIdsInText(text: string): DisplayNameResultType {
        for (const [match] of text.matchAll(ACTOR_ID_REGEX)) {
            if (this.getActorDisplayName(match) === DisplayNameStatus.LOADING) {
                return DisplayNameStatus.LOADING;
            }
        }

        return text.replace(ACTOR_ID_REGEX, (match) => this.getActorDisplayName(match));
    }
}
